import { useEffect, useRef, useState } from "react";
import { MoreHorizontal, Play, Star } from "lucide-react";
import {
  measureDiscover,
  type DiscoverMeasurementView,
} from "./discoverMeasurement";
import { getLocale, t, useLocale } from "@/i18n/locale";
import { Message } from "@/i18n/Message";
import { AppIcon } from "../AppIcon";
import DiscoverCardMetrics from "./DiscoverCardMetrics";
import { qaVisible } from "../qa/qaClient";
import { detailsNavigationTarget, detailsShareTarget, previewNavigationTarget } from "./discoveryUrl";
import PublisherByline from "./PublisherByline";
import RecordedAppPreview, { useRecordedPreviewActions, previewIdentity } from "./RecordedAppPreview";
import type { DiscoveryCardModel } from "./types";
import { compactAppDescription } from "./discoveryCatalogue";
import type { DiscoverFeedback } from "./discoverFeedback";
import { discoverContentBadge } from "./discoverContent";

/**
 * The card's own actions. Same a11y contract as the feed row menu: the
 * trigger reports the menu, Escape and an outside pointerdown close it, and
 * nothing inside selects the card.
 */
function CardFeedbackMenu({ title, onFeedback }: {
  title: string;
  onFeedback: (action: DiscoverFeedback) => void;
}) {
  useLocale();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);
  const choose = (action: DiscoverFeedback) => {
    setOpen(false);
    onFeedback(action);
  };
  return (
    <div className="discovery-card-menu-wrap" ref={wrapRef} onClick={event => event.stopPropagation()}>
      <button type="button" className="discovery-card-menu-btn"
        aria-label={t("More actions for {{value0}}", { value0: title })}
        aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(value => !value)}>
        <MoreHorizontal size={17} aria-hidden="true" />
      </button>
      {open ? (
        <div className="discovery-card-menu" role="menu">
          <button type="button" role="menuitem" className="discovery-card-menu-row" onClick={() => choose("helpful")}>
            <Message message="Helpful" />
          </button>
          <button type="button" role="menuitem" className="discovery-card-menu-row" onClick={() => choose("not_interested")}>
            <Message message="Not interested" />
          </button>
          <button type="button" role="menuitem" className="discovery-card-menu-row" onClick={() => choose("hidden")}>
            <Message message="Don’t show this app" />
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default function DiscoverFeedCard({ entry, selected, onSelect, onNavigate, launchFailed = false, iconSize = 24, position, view, onFeedback }: {
  entry: DiscoveryCardModel;
  selected: boolean;
  launchFailed?: boolean;
  iconSize?: number;
  /** 1-based place in the rendered feed, recorded on the impression. */
  position?: number;
  /** Which surface produced this card, recorded on the impression. */
  view?: DiscoverMeasurementView;
  /** Feedback menu; rendered only where the surface can act on a choice. */
  onFeedback?: (action: DiscoverFeedback) => void;
  onSelect: () => void;
  onNavigate?: (route: string) => void;
}) {
  useLocale();
  const element=useRef<HTMLElement>(null);
  useEffect(()=>{
    if(entry.selection.kind!=="discover"||!entry.publishedVersion||!element.current||typeof IntersectionObserver==="undefined")return;
    const observer=new IntersectionObserver(entries=>{if(entries.some(value=>value.isIntersecting&&value.intersectionRatio>=0.5)){
      measureDiscover(entry.selection.id,entry.publishedVersion!,"impression",{position,view});observer.disconnect();
    }},{threshold:0.5});observer.observe(element.current);return()=>observer.disconnect();
  },[entry.selection.id,entry.selection.kind,entry.publishedVersion,position,view]);
  const previews = useRecordedPreviewActions();
  const previewId = previewIdentity(entry);
  const rating = entry.aiRating;
  const score = rating?.average == null ? null : new Intl.NumberFormat(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(rating.average);
  const copy = (
    <span className="discovery-card-copy">
      <span className="discovery-card-title">
        <span className="discovery-card-icon" aria-hidden="true">
          <AppIcon name={entry.title} seed={entry.selection.id} size={iconSize} listingIconUrl={entry.iconUrl} />
        </span>
        <strong>{entry.title}</strong>{selected ? <small><Message message={"Open"} /></small> : null}
      </span>
      <span className="discovery-card-description" data-nosnippet="">{compactAppDescription(entry.description)}</span>
      {entry.usageNotice ? <span className="discovery-card-notice"><Message message={entry.usageNotice} /></span> : null}
    </span>
  );
  const badge = discoverContentBadge(entry);
  const failed = launchFailed ? <p className="app-launch-failed-label" role="status"><Message message="Launch failed" /></p> : null;
  const meta = (
    <div className="discovery-card-meta">
      {qaVisible() && entry.selection.kind === "discover" ? (
        <a className="discovery-card-rating"
          title={t("AI rating")}
          href={detailsNavigationTarget(entry.selection, "reviews")}
          aria-label={score
            ? t("AI review for {{value0}}: {{value1}} out of 5, {{value2}} rated reviews", { value0: entry.title, value1: score, value2: rating!.rated_count })
            : t("AI review for {{value0}}: Not yet rated", { value0: entry.title })}
          onClick={onNavigate ? event => { event.preventDefault(); onNavigate(detailsNavigationTarget(entry.selection, "reviews")); } : undefined}>
          <span className="discovery-card-rating-source" aria-hidden="true">AI</span><Star size={12} aria-hidden="true" /><span className="discovery-card-rating-score">{score ?? "—"}</span>
        </a>
      ) : null}
      <DiscoverCardMetrics title={entry.title} stats={entry.stats} publishedAt={entry.publishedAt} compact />
      <PublisherByline developers={entry.developers ?? []} onNavigate={onNavigate} />
    </div>
  );
  return (
    <article ref={element} className={`discovery-card${selected ? " discovery-card--selected" : ""}`}
      onPointerEnter={() => previews?.hover(previewId)} onPointerLeave={() => previews?.leave(previewId)}
      onClick={event => { if (!(event.target as Element).closest("a, button, summary")) onSelect(); }}>
        {/* A real link to the app's public page, so the card can be crawled and
            opened in a new tab; a plain click opens it here as before. */}
        <a className="discovery-card-select" href={detailsShareTarget(entry.selection) ?? previewNavigationTarget(entry.selection)}
          onClick={event => {
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
            event.preventDefault();
            onSelect();
          }}
          aria-current={selected ? "true" : undefined} aria-label={t("Select {{value0}}", { value0: entry.title })}>
          <RecordedAppPreview entry={entry} />
          {badge ? <span className={`discovery-card-badge discovery-card-badge--${badge.tone}`}>{t(badge.label)}</span> : null}
          {copy}
        </a>
        {failed}
        {meta}
        {onFeedback && entry.selection.kind === "discover"
          ? <CardFeedbackMenu title={entry.title} onFeedback={onFeedback} />
          : null}
        <button type="button" className="discovery-card-open" onClick={onSelect}
          aria-label={t("Open {{value0}}", { value0: entry.title })}>
          <Play size={16} aria-hidden="true" />
        </button>
    </article>
  );
}
